/**
 * The scenarios a case-level run stamps on its configurations when it starts
 * (EvaluationCalculation#stamp_case_level_scenarios!), and the one rule for reading them back.
 */
export type StampedScenarioKey = 'type_curve_scenario_id' | 'dca_scenario_id';

type StampedConfigurations = Partial<
  Record<StampedScenarioKey, number | null>
> & {
  evaluation_case_id?: number | null;
};

/**
 * The scenario a run stamped under `key`: null for Legacy (a stamped null), undefined when the
 * run carries no stamp (a run from before the key existed), which a caller then answers from the
 * case.
 *
 * @param {object | null | undefined} configurations - The run's configurations.
 * @param {StampedScenarioKey} key - The stamped key.
 * @return {number | null | undefined} The stamped scenario, null, or undefined.
 */
export function stampedScenarioId(
  configurations: StampedConfigurations | null | undefined,
  key: StampedScenarioKey
): number | null | undefined {
  if (!configurations || !(key in configurations)) return undefined;
  return configurations[key] ?? null;
}

/**
 * The scenario a completed run of the selected case started with, under `key`. Undefined when no
 * such run is shown, so the case's current scenario applies:
 * - the run hasn't completed (a first run, or a re-run whose configurations already carry the
 *   new stamp while its results are still the previous run's);
 * - it is another case's run, or no case is selected;
 * - it carries no stamp.
 *
 * @param {object | null | undefined} detail - The shown run's status and configurations.
 * @param {number | null | undefined} evaluationCaseId - The selected case.
 * @param {StampedScenarioKey} key - The stamped key.
 * @return {number | null | undefined} The run's scenario, null for Legacy, or undefined.
 */
export function runCaseScenarioId(
  detail:
    | { status?: string; configurations?: StampedConfigurations | null }
    | null
    | undefined,
  evaluationCaseId: number | null | undefined,
  key: StampedScenarioKey
): number | null | undefined {
  if (
    detail?.status !== 'Completed' ||
    evaluationCaseId === null ||
    evaluationCaseId === undefined
  ) {
    return undefined;
  }
  if (detail.configurations?.evaluation_case_id !== evaluationCaseId) {
    return undefined;
  }
  return stampedScenarioId(detail.configurations, key);
}
